

import React, { useState } from 'react';
import {Box,Card,CardContent,Typography,Chip,IconButton,Stack,Tabs,Tab,Alert,TextField,InputAdornment,Button
} from '@mui/material';
import { Phone, Refresh, Description, Search } from '@mui/icons-material';
import dayjs from 'dayjs';

const OrderList = ({ orders, loading, onOrderSelect, onRefresh }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');

  const getStatusColor = (status) => {
    const statusMap = {
      'PENDING ACCEPTANCE': '#ffc107',
      'READY FOR PICKUP': '#007bff',
      'PICKED UP': '#17a2b8',
      'PAYMENT DUE': '#dc3545',
      'PAYMENT COLLECTED': '#28a745',
      'OUT FOR DELIVERY': '#007bff',
      'DELIVERED': '#28a745',
      'ISSUE REPORTED': '#dc3545',
      'report-dispatched': '#007bff',
      'report-out-for-delivery': '#17a2b8',
      'report-delivered': '#28a745'
    };
    return statusMap[status] || '#6c757d';
  };

  const formatAddress = (fullAddress) => {
    if (!fullAddress) return {};
  
    const parts = fullAddress.split(',').map(p => p.trim());
  
    return {
      block: parts[0] || '',
      flatNo: parts[1] || '',
      community: parts[2] || '',
      remaining: parts.slice(3).join(', ')
    };
  };
  
  const getDeliveryStatus = (order) => {
    return order.orderType === 'booking'
      ? order.orderStatus
      : order.deliveryStatus;
  };

  const isDelivered = (order) => {
    if (order.deliveryType === 'report') {
      return order.orderStatus === 'report-delivered';
    }
    return order.deliveryStatus === 'delivered' || order.orderStatus === 'delivered';
  };

  const isPendingAcceptance = (order) => order.assignmentStatus === 'pending';

  const isReadyForPickup = (order) => {
    if (order.deliveryType === 'report') {
      return order.assignmentStatus === 'accepted' && 
             order.orderStatus === 'report-dispatched';
    }
    
    return order.assignmentStatus === 'accepted' &&
      order.deliveryStatus !== 'picked_up' &&
      order.deliveryStatus !== 'delivered' &&
      order.orderStatus !== 'sample-collected';
  };

  const isInProgress = (order) => {
    if (order.deliveryType === 'report') {
      return order.orderStatus === 'report-out-for-delivery' && 
             order.orderStatus !== 'report-delivered';
    }
    
    return (order.deliveryStatus === 'picked_up' || 
            order.orderStatus === 'sample-collected') &&
      !isDelivered(order);
  };

  const filteredOrders = orders.filter((order) => {
    // Tab filter
    let matchesTab = true;
    if (activeTab === 0) matchesTab = true; // All orders
    else if (activeTab === 1) matchesTab = isPendingAcceptance(order);
    else if (activeTab === 2) matchesTab = isReadyForPickup(order);
    else if (activeTab === 3) matchesTab = isInProgress(order);
    else if (activeTab === 4) matchesTab = isDelivered(order);
  
    // Search filter
    let matchesSearch = true;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      matchesSearch = 
        order.orderId?.toLowerCase().includes(query) ||
        order.customerName?.toLowerCase().includes(query) ||
        order.customerPhone?.includes(query) ||
        order.serviceName?.toLowerCase().includes(query) ||
        order.id?.toString().includes(query) ||
        order.customerAddress?.fullAddress?.toLowerCase().includes(query);
    }

    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const today = dayjs().startOf('day');
      const orderDate = order.orderType === 'medicine' && order.customerAddress?.delivery_time
        ? dayjs(order.customerAddress.delivery_time).startOf('day')
        : order.date 
          ? dayjs(order.date).startOf('day')
          : order.assignedAt
            ? dayjs(order.assignedAt).startOf('day')
            : null;

      if (orderDate) {
        if (dateFilter === 'today') {
          matchesDate = orderDate.isSame(today, 'day');
        } else if (dateFilter === 'tomorrow') {
          matchesDate = orderDate.isSame(today.add(1, 'day'), 'day');
        } else if (dateFilter === 'week') {
          matchesDate = orderDate.isBefore(today.add(7, 'day')) && orderDate.isAfter(today.subtract(1, 'day'));
        } else if (dateFilter === 'past') {
          matchesDate = orderDate.isBefore(today, 'day');
        }
      }
    }
  
    return matchesTab && matchesSearch && matchesDate;
  });

  const getOrderTypeLabel = (order) => {
    if (order.orderType === 'medicine') {
      return '💊 Medicine';
    }
    if (order.deliveryType === 'report') {
      return '📋 Report Delivery';
    }
    if (order.deliveryType === 'sample') {
      return '🧪 Sample Collection';
    }
    return '📦 Order';
  };

  // Calculate today's deliveries count
  const getTodaysDeliveriesCount = () => {
    const today = dayjs().startOf('day');
    return orders.filter(o => {
      const orderDate = o.orderType === 'medicine' && o.customerAddress?.delivery_time
        ? dayjs(o.customerAddress.delivery_time).startOf('day')
        : o.date ? dayjs(o.date).startOf('day') : null;
      return orderDate?.isSame(today, 'day');
    }).length;
  };

  return (
    <Box>
      {/* Header */}
      <Stack spacing={2} mb={2}>
        {/* Top Row: Title and Actions */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight={600}>
            My Orders
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {(searchQuery.trim() || dateFilter !== 'all') && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setDateFilter('all');
                }}
                sx={{
                  borderColor: '#dc3545',
                  color: '#dc3545',
                  '&:hover': { 
                    borderColor: '#bd2130',
                    bgcolor: '#dc354510' 
                  }
                }}
              >
                Clear Filters
              </Button>
            )}
            <IconButton 
              onClick={onRefresh} 
              disabled={loading}
              sx={{
                bgcolor: '#006D77',
                color: '#fff',
                '&:hover': { bgcolor: '#005561' },
                '&:disabled': { bgcolor: '#ccc' }
              }}
            >
              <Refresh />
            </IconButton>
          </Stack>
        </Stack>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search by Order ID, Customer Name, Phone, Service, or Address..."
          variant="outlined"
          size="medium"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#006D77' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {borderColor: '#006D77',},
              '&.Mui-focused fieldset': { borderColor: '#006D77', },
            },
          }}
        />

        {/* Active Filters Display */}
        {(searchQuery.trim() || dateFilter !== 'all') && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Active Filters:
            </Typography>
            {searchQuery.trim() && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery('')}
                color="primary"
                variant="outlined"
              />
            )}
            {dateFilter !== 'all' && (
              <Chip
                label={`Date: ${dateFilter === 'today' ? 'Today' : dateFilter === 'tomorrow' ? 'Tomorrow' : dateFilter === 'week' ? 'This Week' : 'Past Dates'}`}
                size="small"
                onDelete={() => setDateFilter('all')}
                color="secondary"
                variant="outlined"
              />
            )}
          </Stack>
        )}
      </Stack>

      {/* Summary Statistics */}
      <Box sx={{ 
        mb: 2, 
        p: 2, 
        bgcolor: '#f8fafc', 
        borderRadius: 2,
        border: '1px solid #e2e8f0'
      }}>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Orders
            </Typography>
            <Typography variant="h6" fontWeight={600} color="primary">
              {filteredOrders.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Today's Deliveries
            </Typography>
            <Typography variant="h6" fontWeight={600} color="success.main">
              {getTodaysDeliveriesCount()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Pending
            </Typography>
            <Typography variant="h6" fontWeight={600} color="warning.main">
              {orders.filter(isPendingAcceptance).length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              In Progress
            </Typography>
            <Typography variant="h6" fontWeight={600} color="info.main">
              {orders.filter(isInProgress).length}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(e, val) => setActiveTab(val)} 
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          '& .MuiTab-root': {color: '#6c757d',textTransform: 'none',fontWeight: 500,},
          '& .MuiTab-root.Mui-selected': {color: '#006D77',fontWeight: 600,},
          '& .MuiTabs-indicator': {backgroundColor: '#006D77', height: 3,},
        }}
      >
        <Tab label={`All (${orders.length})`} />
        <Tab label={`Pending (${orders.filter(isPendingAcceptance).length})`} />
        <Tab label={`Ready (${orders.filter(isReadyForPickup).length})`} />
        <Tab label={`In Progress (${orders.filter(isInProgress).length})`} />
        <Tab label={`Delivered (${orders.filter(isDelivered).length})`} />
      </Tabs>

      {/* Date Filter Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
          Filter by Date:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          <Chip
            label="All Dates"
            onClick={() => setDateFilter('all')}
            color={dateFilter === 'all' ? 'primary' : 'default'}
            variant={dateFilter === 'all' ? 'filled' : 'outlined'}
            sx={{ fontWeight: dateFilter === 'all' ? 600 : 400 }}
          />
          <Chip
            label="📅 Today"
            onClick={() => setDateFilter('today')}
            color={dateFilter === 'today' ? 'success' : 'default'}
            variant={dateFilter === 'today' ? 'filled' : 'outlined'}
            sx={{ fontWeight: dateFilter === 'today' ? 600 : 400 }}
          />
          <Chip
            label="📆 Tomorrow"
            onClick={() => setDateFilter('tomorrow')}
            color={dateFilter === 'tomorrow' ? 'info' : 'default'}
            variant={dateFilter === 'tomorrow' ? 'filled' : 'outlined'}
            sx={{ fontWeight: dateFilter === 'tomorrow' ? 600 : 400 }}
          />
          <Chip
            label="📊 This Week"
            onClick={() => setDateFilter('week')}
            color={dateFilter === 'week' ? 'secondary' : 'default'}
            variant={dateFilter === 'week' ? 'filled' : 'outlined'}
            sx={{ fontWeight: dateFilter === 'week' ? 600 : 400 }}
          />
        </Stack>
      </Box>

      {/* Order Cards */}
      {filteredOrders.length === 0 ? (
        <Alert severity="info">
          {searchQuery.trim() || dateFilter !== 'all'
            ? `No orders found${searchQuery.trim() ? ` matching "${searchQuery}"` : ''}${
                dateFilter !== 'all' ? ` for ${dateFilter === 'today' ? 'today' : dateFilter === 'tomorrow' ? 'tomorrow' : dateFilter === 'week' ? 'this week' : 'past dates'}` : ''
              }`
            : activeTab === 0 
              ? 'No orders assigned yet' 
              : 'No orders in this category'
          }
        </Alert>
      ) : (
        <Stack spacing={2}>
          {filteredOrders.map((order) => {
            const status = getDeliveryStatus(order);

            return (
              <Card
                key={order.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 },
                  borderLeft: `4px solid ${getStatusColor(status)}`
                }}
                onClick={() => onOrderSelect(order)}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box flex={1}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={600}>
                          {order.orderId}
                        </Typography>
                        
                        {/* Order Type Badge */}
                        <Chip
                          label={getOrderTypeLabel(order)}
                          size="small"
                          sx={{
                            bgcolor: order.deliveryType === 'report' ? '#9c27b0' : 
                                     order.orderType === 'medicine' ? '#2196f3' : '#ff9800',
                            color: '#fff',
                            fontWeight: 600
                          }}
                        />
                        
                        {/* Status Badge */}
                        <Chip
                          label={status}
                          size="small"
                          sx={{
                            bgcolor: getStatusColor(status),
                            color: '#fff',
                            fontWeight: 600
                          }}
                        />
                        
                        {/* Payment Badge */}
                        {order.isPaid !== undefined && (
                          <Chip
                            label={order.isPaid ? 'PAID' : 'COD'}
                            size="small"
                            color={order.isPaid ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        )}
                        
                        {/* Report Count Badge */}
                        {order.deliveryType === 'report' && order.reportCount > 0 && (
                          <Chip
                            icon={<Description />}
                            label={`${order.reportCount} Report${order.reportCount > 1 ? 's' : ''}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {order.customerName}
                      </Typography>

                      {/* Booking date */}
                      {order.orderType === 'booking' && order.date && (
                        <Typography variant="body2" color="text.secondary">
                          📅 {dayjs(order.date).format('DD MMM YYYY, hh:mm A')}
                        </Typography>
                      )}

                      {/* Medicine delivery time */}
                      {order.orderType === 'medicine' &&
                        (order.customerAddress?.delivery_time ||
                        order.customerAddress?.delivery_time_option) && (
                          <Typography variant="body2" color="text.secondary">
                            ⏰ Scheduled Delivery
                            {order.customerAddress.delivery_time_option
                              ? ` (${order.customerAddress.delivery_time_option})`
                              : ''}
                            {order.customerAddress.delivery_time
                              ? `: ${order.customerAddress.delivery_time}`
                              : ''}
                          </Typography>
                      )}

                      <Stack direction="row" spacing={2} mt={1} flexWrap="wrap">
                        <Typography variant="body2">
                          {order.orderType === 'medicine'
                            ? `${order.items?.length || 0} items`
                            : order.serviceName || 'Lab Test'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          ₹{order.totalAmount}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: order.paymentMethod === 'cod' ? '#dc3545' : '#28a745' 
                        }}>
                          {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 
                           order.paymentMethod ? order.paymentMethod.toUpperCase() : 'Prepaid'}
                        </Typography>
                      </Stack>

                      {/* Address */}
                      {order.customerAddress?.fullAddress && (() => {
                        const { block, flatNo, community, remaining } =
                          formatAddress(order.customerAddress.fullAddress);

                        return (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block', lineHeight: 1.6 }}
                          >
                            {block && <div><strong>Block:</strong> {block}</div>}
                            {flatNo && <div><strong>Flat No:</strong> {flatNo}</div>}
                            {community && <div><strong>Community:</strong> {community}</div>}
                            {remaining && <div>📍 {remaining}</div>}
                          </Typography>
                        );
                      })()}
                      
                      {/* Assigned time for report delivery */}
                      {order.deliveryType === 'report' && order.assignedAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          🕒 Assigned: {dayjs(order.assignedAt).format('DD MMM, hh:mm A')}
                        </Typography>
                      )}
                    </Box>

                    <IconButton 
                      href={`tel:${order.customerPhone}`}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        bgcolor: '#006D77',
                        color: '#fff',
                        '&:hover': { bgcolor: '#005561' }
                      }}
                    >
                      <Phone />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default OrderList;